import React from "react";

import { Block, Visibility, Edit, HourglassEmpty } from "@mui/icons-material";
import {
  Box,
  Stepper,
  Step,
  StepButton,
  StepLabel,
  Tooltip,
} from "@mui/material";
import { colors } from "@rubi/ui";

import { PermissionLevel } from "./types";
import { getPermissionLevel, getActiveStep } from "./utils";

type StepLevel = PermissionLevel | "under_review";

const getStepColor = (level: StepLevel, isActive: boolean): string => {
  if (!isActive) return "divider";
  switch (level) {
    case "none":
      return colors.legacy.coral;
    case "under_review":
      return colors.primary.light;
    case "read":
      return colors.warning.light;
    case "full":
      return colors.success.light;
    default:
      return "divider";
  }
};

interface PermissionStepperProps {
  canView: boolean;
  canModify: boolean;
  onPermissionChange: (level: PermissionLevel) => void;
  disabled?: boolean;
  maxPermission?: number; // 0=none, 1=canview, 2=canmodify, default is 2
  canShareToGroup?: boolean; // Permission to share to this group
  canShareReason?: string; // Reason if cannot share
  canRemoveFromBulletin?: boolean; // Permission to remove this group from bulletin
  canRemoveReason?: string; // Reason if cannot remove
  hasPendingShare?: boolean; // Whether there's a pending share request
  isPublic?: boolean; // Whether this is a public group
}

export const PermissionStepper: React.FC<PermissionStepperProps> = ({
  canView,
  canModify,
  onPermissionChange,
  maxPermission = 2,
  canShareToGroup = true,
  canShareReason = "",
  canRemoveFromBulletin = true,
  canRemoveReason = "",
  hasPendingShare = false,
  isPublic = false,
}) => {
  const currentLevel = getPermissionLevel(canView, canModify);

  // Determine descriptions based on permissions
  const getStepDescription = (level: PermissionLevel) => {
    if (level === "none") {
      if (currentLevel === "none") {
        return "Group does not have access to this bulletin";
      }
      if (currentLevel === "read") {
        if (!canRemoveFromBulletin && canRemoveReason) {
          return canRemoveReason;
        } else {
          return "Remove view access from this group";
        }
      }
      if (currentLevel === "full") {
        // is_owner group. Cannot change at all.
        if (!canRemoveFromBulletin && canRemoveReason) {
          return canRemoveReason;
        }
      }
      return ``;
    } else if (level === "read") {
      if (currentLevel === "none") {
        if (hasPendingShare) {
          return "Cannot share while there is a pending share request";
        } else if (!canShareToGroup && canShareReason) {
          return canShareReason;
        } else if (isPublic) {
          return "Give view access to this public group. You will be asked to configure access before the Bulletin is released to the public.";
        } else {
          return "Give view access to this group. User will be able to view this bulletin";
        }
      }
      if (currentLevel === "read") {
        return "User in this Group can view this bulletin";
      }
      if (currentLevel === "full") {
        // NOTE: now only owner group can have full access, but owner group
        // cannot be removed
        return "User can view this bulletin but cannot modify.";
      }
      return "";
    } else if (level === "full") {
      // we don't have full access level icon now.
    }
    return "";
  };

  const isButtonDisabled = (level: PermissionLevel) => {
    // Already at this level - no action needed
    if (level === currentLevel) {
      return true;
    }

    // Switching to "none" (removing access)
    if (level === "none") {
      return !canRemoveFromBulletin;
    }

    // Switching to "read" or "full" (granting/modifying access)
    if (level === "read" || level === "full") {
      // If currently "none", need permission to share
      if (currentLevel === "none") {
        // Disable if there's a pending share request
        if (hasPendingShare) {
          return true;
        }
        return !canShareToGroup;
      }
      // If already have access, just switching between read/full - allowed
      return false;
    }

    return false;
  };

  const allSteps: {
    level: StepLevel;
    label: string;
    icon: React.ReactNode;
    description: string;
  }[] = [
    {
      level: "none" as StepLevel,
      label: "No Access",
      icon: <Block />,
      description: getStepDescription("none"),
    },
    {
      level: "read" as StepLevel,
      label: "Read Only",
      icon: <Visibility />,
      description: getStepDescription("read"),
    },
    {
      level: "full" as StepLevel,
      label: "Full Access",
      icon: <Edit />,
      description: getStepDescription("full"),
    },
  ];

  // Filter steps based on maxPermission
  // maxPermission: 0=none only, 1=none+read, 2=none+read+full
  const getFilteredSteps = () => {
    let filteredSteps = [...allSteps];

    // Filter based on maxPermission level
    if (maxPermission === 0) {
      filteredSteps = filteredSteps.filter((step) => step.level === "none");
    } else if (maxPermission === 1) {
      filteredSteps = filteredSteps.filter((step) => step.level !== "full");
    }
    // maxPermission === 2 allows all steps (default behavior)

    // Inject "Under Review" step between "No Access" and "Read Only" when pending
    if (hasPendingShare && currentLevel === "none") {
      const noneIdx = filteredSteps.findIndex((s) => s.level === "none");
      if (noneIdx !== -1) {
        filteredSteps.splice(noneIdx + 1, 0, {
          level: "under_review" as StepLevel,
          label: "Under Review",
          icon: <HourglassEmpty />,
          description:
            "Sharing request is pending approval from group moderators",
        });
      }
    }

    return filteredSteps;
  };

  const steps = getFilteredSteps();

  const activeStep =
    hasPendingShare && currentLevel === "none"
      ? steps.findIndex((s) => s.level === "under_review")
      : getActiveStep(currentLevel, maxPermission);

  return (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          "& .MuiStepConnector-line": {
            borderTopWidth: 2,
          },
          "& .MuiStepLabel-label": {
            fontSize: "0.75rem",
          },
        }}
      >
        {steps.map((step, index) => {
          const isActive = index === activeStep;
          const stepDisabled =
            step.level === "under_review"
              ? true
              : isButtonDisabled(step.level as PermissionLevel);
          const stepColor = getStepColor(step.level, isActive);

          return (
            <Step key={step.level}>
              <Tooltip title={step.description} arrow placement="top">
                <span>
                  <StepButton
                    onClick={() =>
                      !stepDisabled &&
                      onPermissionChange(step.level as PermissionLevel)
                    }
                    disabled={stepDisabled}
                    sx={{
                      "& .MuiStepIcon-root": {
                        color: stepColor,
                        "&:hover": {
                          color: !stepDisabled ? stepColor : "divider",
                        },
                      },
                    }}
                  >
                    <StepLabel
                      StepIconComponent={() => (
                        <Box
                          sx={{
                            color: stepColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            fontSize: "1rem",
                          }}
                        >
                          {step.icon}
                        </Box>
                      )}
                    >
                      {/* Remove text label, show only icon */}
                    </StepLabel>
                  </StepButton>
                </span>
              </Tooltip>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};
